import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import Button from '../components/Button';
import MoleculeDecor from '../components/MoleculeDecor';
import { useAuth } from '../context/AuthContext';
import { getProfile } from '../api/profile';
import { CLUB_BENEFITS } from '../constants/philosophy';
import styles from './Club.module.css';

// Форматы участия в клубе
const MEMBERSHIP_TIERS = [
  {
    id: 1,
    name: 'Базовый',
    price: '2 000 ₽/мес',
    description: 'Станьте частью сообщества людей, которые берут здоровье под контроль',
    features: [
      'Доступ к закрытым лекциям (2 в месяц) и вебинарам с ведущими врачами',
      '2 раза в месяц ответы на ваши вопросы от Терушкина Р.А.',
      'Персональные консультации со специалистами клуба (оплачиваются отдельно)',
      'Программы управления здоровьем на основе ваших данных',
      'Закрытое сообщество единомышленников, заботящихся о своём здоровье',
      'Поддержка и челленджи от таких же увлечённых здоровьем участников клуба',
      'Дополнительные скидки на лабораторные анализы и диагностику, а также специальные партнёрские предложения, которых нет нигде',
      'Ежемесячные отчёты о состоянии вашего здоровья и прогрессе',
    ],
    highlight: false,
  },
  {
    id: 2,
    name: 'Расширенный',
    description: 'Полный доступ ко всем возможностям клуба и персональному сопровождению',
    features: [
      'Всё из базового тарифа',
      'Персональные консультации с врачами',
      'Индивидуальная программа здоровья',
      'Ежемесячный мониторинг биомаркеров',
      'Приоритетная запись к специалистам',
      'Ежемесячный отчёт о состоянии здоровья',
    ],
    highlight: true,
  },
];

const Club = () => {
  const { token, isAuthenticated } = useAuth();
  const [isPaid, setIsPaid] = useState(false);

  useEffect(() => {
    if (!token) {
      setIsPaid(false);
      return;
    }
    getProfile(token)
      .then((profile) => {
        const payUntil = profile.pay_until ? new Date(profile.pay_until) : null;
        setIsPaid(payUntil !== null && payUntil > new Date());
      })
      .catch(() => setIsPaid(false));
  }, [token]);

  const ctaLink = isPaid ? '/news' : isAuthenticated ? '/payment' : '/login';
  const ctaLabel = isPaid ? 'Перейти в новости' : isAuthenticated ? 'Оформить подписку — 2 000 ₽/мес' : 'Зарегистрироваться';

  return (
  <>
    {/* Hero-шапка */}
    <section className={styles.pageHero}>
      <MoleculeDecor style={{ left: '2%', top: '50%', transform: 'translateY(-50%) rotate(-15deg)', width: 190, height: 190, opacity: 0.40 }} />
      <div className="container">
        <p className={styles.pageHeroLabel}>Членство</p>
        <h1 className={styles.pageHeroTitle}>Клуб Терушкин ОС</h1>
        <p className={styles.pageHeroSubtitle}>
          Закрытое сообщество людей, которые управляют своим здоровьем осознанно.
          Доступ к лучшим специалистам, персональным программам и поддерживающему окружению.
        </p>
        <Link to={ctaLink}>
          <Button variant="primary" size="lg">
            {ctaLabel}
          </Button>
        </Link>
      </div>
    </section>

    {/* Преимущества */}
    <Section
      bg="white"
      title="Что вы получаете"
      subtitle="Каждый участник клуба получает доступ к уникальным возможностям"
    >
      <div className={styles.benefitsGrid}>
        {CLUB_BENEFITS.map((benefit, i) => (
          <div key={i} className={styles.benefitItem}>
            <div className={styles.benefitIconWrap} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <p className={styles.benefitText}>{benefit}</p>
          </div>
        ))}
      </div>
    </Section>

    {/* Форматы участия */}
    <Section
      bg="gray"
      title="Форматы участия"
      subtitle="Выберите уровень, который подходит именно вам"
    >
      <div className={styles.tiersGrid}>
        {MEMBERSHIP_TIERS.map((tier) => (
          <div
            key={tier.id}
            className={`${styles.tier} ${tier.highlight ? styles.tierHighlight : ''}`}
          >
            {tier.highlight && (
              <div className={styles.tierBadge}>В разработке</div>
            )}
            <h3 className={styles.tierName}>{tier.name}</h3>
            {tier.price && <p className={styles.tierPrice}>{tier.price}</p>}
            <div className={tier.highlight ? styles.tierBodyBlurred : undefined}>
              <p className={styles.tierDesc}>{tier.description}</p>
              <ul className={styles.tierFeatures}>
                {tier.features.map((f, i) => (
                  <li key={i} className={styles.tierFeature}>
                    <span className={styles.tierCheck} aria-hidden="true">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            {tier.highlight ? (
              <Button
                variant="primary"
                size="md"
                className={styles.tierButton}
                disabled
              >
                В разработке
              </Button>
            ) : (
              <Link to={ctaLink}>
                <Button variant="outline" size="md" className={styles.tierButton}>
                  {isPaid ? 'Перейти в новости' : isAuthenticated ? 'Оформить подписку — 2 000 ₽/мес' : 'Вступить'}
                </Button>
              </Link>
            )}
          </div>
        ))}
      </div>
    </Section>

    {/* Условия вступления */}
    <Section bg="white" title="Как вступить" subtitle="Три простых шага к управлению своим здоровьем.
  Начни уже сегодня, чтобы завтра быть ближе к цели" >
      <div className={styles.howToGrid}>
        {[
          {
            step: '1',
            title: 'Зарегистрируйтесь',
            desc: 'Создайте аккаунт на сайте — это займёт меньше минуты. Нужен только email и пароль.',
          },
          {
            step: '2',
            title: 'Заполните профиль',
            desc: 'Укажите основные данные о себе, чтобы наши специалисты могли подготовиться к первой встрече.',
          },
          {
            step: '3',
            title: 'Начните путь',
            desc: 'Запишитесь на вводную консультацию и получите персональный план на первые три месяца.',
          },
        ].map(({ step, title, desc }) => (
          <div key={step} className={styles.howToStep}>
            <div className={styles.howToStepNum}>{step}</div>
            <h3 className={styles.howToStepTitle}>{title}</h3>
            <p className={styles.howToStepDesc}>{desc}</p>
          </div>
        ))}
      </div>
      <div className={styles.howToCta}>
        <Link to={ctaLink}>
          <Button variant="primary" size="lg">
            {isPaid ? 'Перейти в новости' : isAuthenticated ? 'Оформить подписку — 2 000 ₽/мес' : 'Зарегистрироваться сейчас'}
          </Button>
        </Link>
      </div>
    </Section>
  </>
  );
};

export default Club;
